---
title: Software Developer
status: open
type: Full-Time
location: On-Site — Hancock, Michigan
compensation: $70,000 – $105,000 / year
summary: Design, build, and ship custom software for real clients — front end to back end — alongside an experienced team that values doing things right the first time.
# This posting keeps the address it had on the old site.
path: /full-stack-developer-entry-level/
---

## Compensation

**$70,000 – $105,000 annually**, depending on experience, demonstrated capability, and scope of responsibility

## Benefits

- Health insurance
- **Dental and vision insurance – 100% paid by the company**
- **401(k) with company match up to 4%**
- Paid Time Off (PTO)
- **7 paid holidays**
- Flexible work environment
- Relocation assistance, if needed
- Office perks: pool table, ping pong, foosball, and payday cookouts

## Role Summary

We’re a growing small business (typically 17–20 employees) looking for a capable, motivated **Software Developer** to design, build, and ship custom software for real clients solving real problems.

Xeratec clients range from startups to Fortune 500 companies, and projects range from **custom apps to SaaS products to IoT projects**. You’ll work across the stack — front end to back end — alongside an experienced team that values doing things right the first time.

**The foundation of this role is writing quality software that solves the client’s actual problem. All other responsibilities build on that foundation.**

This role is well-suited for a developer who wants variety in their work, real ownership of what they build, and room to grow their skills across languages, stacks, and industries.

## Why This Role Exists

**This role exists to build software that solves our clients’ problems the first time.**

Over half of companies that hire software development partners are unhappy with the results. Xeratec exists to be the exception — we create personal relationships with our customers, commit to understanding the real problem at the start, and check in often along the way. It’s why 90% of our clients are repeat customers.

As a developer here, your work is the product. When it’s done well, clients come back, projects move faster, and the whole team benefits.

## Who This Role Is For

This role is well-suited for someone who:

- Enjoys analyzing and solving problems, not just writing code
- Takes pride in shipping work that’s tested, deployed, and actually used
- Is comfortable taking ownership without needing detailed step-by-step instructions
- Isn’t afraid to tackle different languages and technology stacks
- Communicates clearly and can interact with clients on their level
- Brings intellectual curiosity and an appetite for learning

**This is not a heads-down, ticket-only role.** It’s a build-focused role with real responsibility, client contact, and room to grow.

## How This Role Supports the Team

- Designs, develops, tests, and deploys software solutions that clients rely on
- Contributes across front-end and back-end stacks so projects keep moving
- Participates in Agile ceremonies — sprint planning, standups, retrospectives — so work stays predictable
- Communicates progress and asks the right questions early, reducing rework and surprises
- Helps evaluate new tools and frameworks so the team stays current

## Key Responsibilities

### Software Development

- Design, develop, test, and deploy full-stack software solutions
- Work in technologies such as Angular, React, Java, .NET, MongoDB, MS SQL Server, Oracle, mobile development, and IoT
- Flex into other languages and stacks depending on project requirements
- Write maintainable, well-structured code with appropriate testing

### Project & Process

- Participate in Agile methodology: sprint planning, complexity analysis, standups, and sprint retrospectives
- Estimate work honestly and flag risks early
- Document solutions so others can build on your work

### Client & Team Collaboration

- Interact with clients professionally and translate technical concepts to their level
- Collaborate with teammates on architecture and code review
- Support handoffs to onboarding and support so clients succeed after launch

### Growth & Learning

- Stay current with new and emerging software and development frameworks
- Learn system architecture and soft skills from an experienced team of professionals

## What Success Looks Like

- Software ships on time, works as intended, and solves the client’s real problem
- Code is clean, tested, and maintainable
- Clients trust you and come back for more work
- You grow into new stacks and bigger ownership over time
- Teammates gain confidence knowing your pieces will be done well

## Qualifications (Required)

- **Programming experience in one or more of these areas preferred:**Angular, React, Java, .NET, MongoDB, MS SQL Server, Oracle, mobile development, IoT
- Ability to design, develop, test, and deploy software solutions
- Ability to develop front-end and back-end technology stacks preferred
- Experience or familiarity with Agile methodology, including sprint planning, complexity analysis, standups, and sprint retrospectives
- Flexibility to develop in other languages and software stacks depending on project requirements
- Willingness to stay current with new and emerging software and development frameworks
- Intellectual curiosity and an appetite for learning — we’ll provide the training needed to excel
- **Ability to work on-site in Hancock, Michigan** (relocation assistance available)

**A degree in Computer Science, Software Engineering, or a related field is helpful, but not required.** We’re most interested in candidates who demonstrate outstanding development potential proven by great results, interesting projects, open-source contributions, or superior academic results.

## Growth Opportunity

As trust and confidence are built, this role can grow to include larger projects, architectural ownership, client leadership, and increased compensation tied to expanded scope.

Growth is based on **capability, initiative, and consistent follow-through**, not tenure.

## Why This Role Is a Good Fit

- Meaningful, varied work — different clients, industries, and stacks
- Clear expectations and supportive, experienced leadership
- Real ownership of what you build
- A workplace that values doing things right and taking pride in your work
